import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ThemePalette } from '@angular/material/core';
import { ProgressBarMode } from '@angular/material/progress-bar';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

import {
  LayoutUtilsService,
  LoaderService,
  RequestService,
} from '../../services';

interface XlsxUploaderDialogData {
  title: string;
  /** API path relative to authURL, e.g. '/user/import-xlsx' */
  path: string;
  /** Whether to send the file name as additional "name" field (defaults to true) */
  addName?: boolean;
}

@Component({
  selector: 'app-xlsx-uploader-dialog-modal',
  templateUrl: './custom-xlsx-uploader-dialog.component.html',
  styleUrls: ['./custom-xlsx-uploader-dialog.component.scss'],
})
export class XlsxUploaderDialogModalComponent {
  public errorMessage = '';
  public loading = false;

  public allowedExtensions: string[] = ['xlsx'];
  public max_size = 5000000; // 5MB

  public path: string;
  public addName = true;

  public results: any = undefined;
  public showMoreInfo = false;

  /** Optional password for password-protected XLSX files. Sent with upload so backend can open the file. */
  public filePassword = '';

  currentFiles: any[] = [];
  selectedFiles: FileList;
  progressInfos: any[] = [];
  progressInfosOld: any[] = [];

  color: ThemePalette = 'primary';
  mode: ProgressBarMode = 'determinate';

  @ViewChild('fileXlsxInput') fileXlsxInput: ElementRef;

  constructor(
    public dialog: MatDialog,
    private loadService: LoaderService,
    private translate: TranslateService,
    private requestService: RequestService,
    private layoutUtilsService: LayoutUtilsService,
    public dialogRef: MatDialogRef<XlsxUploaderDialogModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: XlsxUploaderDialogData
  ) {
    this.path = data.path;
    this.addName = data.addName !== undefined ? data.addName : true;
  }

  closeModal(): void {
    const uploadedProcesses = this.progressInfosOld.filter(
      (itm) => itm.uploaded
    );
    if (uploadedProcesses.length > 0) {
      this.dialogRef.close(uploadedProcesses);
    } else {
      this.dialogRef.close();
    }
  }

  onBrowseFiles(target: any): void {
    if (target.files.length > 0) {
      this.results = undefined;
      this.selectFiles(target);
    }
  }

  selectFiles(target: any): void {
    this.progressInfos = [];
    this.currentFiles = [];
    this.selectedFiles = target.files;
    const file = this.selectedFiles.item(0);
    if (!file) {
      return;
    }

    const currentFile = {
      error: false,
      name: file.name,
      text: file.name.split('.')[0],
      originalFile: file,
      extension: 'xlsx',
      message: this.translate.instant(
        'The file is ready, please click Confirm'
      ),
      messages: [],
    };

    const fileExt = file.name.split('.').pop();
    currentFile.extension = fileExt ? fileExt.toLowerCase() : '';

    if (file.size > this.max_size) {
      currentFile.error = true;
      currentFile.message =
        this.translate.instant('Maximum size allowed is') +
        ' ' +
        this.max_size / 1000000 +
        'MB';
    } else if (this.allowedExtensions.indexOf(currentFile.extension) === -1) {
      currentFile.error = true;
      currentFile.message = this.translate.instant(
        'The file type is not allowed'
      );
    }

    this.currentFiles.push(currentFile);
  }

  deleteFile(index: number): void {
    this.currentFiles.splice(index, 1);
  }

  cancelFile(idx: number): void {
    if (this.progressInfos[idx]['subscription']) {
      this.progressInfos[idx]['subscription'].unsubscribe();
    }
    this.progressInfos[idx].error = true;
    this.progressInfos[idx].complete = true;
    this.progressInfos[idx].value = 0;
    this.progressInfos[idx].message = 'Cancelled';
    this.updateLoadingStatus();
  }

  uploadFiles(): void {
    this.errorMessage = '';
    this.loading = true;
    for (let i = 0; i < this.currentFiles.length; i++) {
      this.upload(i, this.currentFiles[i]);
    }
  }

  uploadFile(idx: number): void {
    this.errorMessage = '';
    this.loading = true;
    this.upload(idx, this.currentFiles[idx]);
  }

  updateLoadingStatus(): void {
    this.loadService.display(false);
    const remainingProcesses = this.progressInfos.filter(
      (itm) => !itm.complete && !itm.error
    );
    if (remainingProcesses.length === 0) {
      this.loading = false;
      this.progressInfosOld = this.progressInfos;
    }
  }

  upload(idx: number, browsed_file: any): void {
    this.loadService.display(true);
    this.progressInfos[idx] = browsed_file;
    this.progressInfos[idx]['value'] = 0;
    this.progressInfos[idx]['uploaded'] = undefined;
    this.progressInfos[idx]['complete'] = false;
    this.progressInfos[idx]['subscription'] = undefined;
    this.progressInfos[idx]['message'] = undefined;

    const passwordToSend = this.filePassword?.trim() || undefined;
    this.progressInfos[idx]['subscription'] = this.requestService
      .onUploadFilesByPathNew(
        this.path,
        browsed_file,
        undefined,
        undefined,
        undefined,
        undefined,
        this.addName,
        passwordToSend
      )
      .subscribe(
        (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.progressInfos[idx].value = Math.round(
              (100 * event.loaded) / (event.total || 1)
            );
          } else if (event instanceof HttpResponse) {
            this.progressInfos[idx].complete = true;
            this.progressInfos[idx].message = this.translate.instant(
              'Completed'
            );
            if (event.body && !event.body.status && event.body.message) {
              this.progressInfos[idx].error = true;
              this.progressInfos[idx].message = this.translate.instant(
                'Could not upload the file'
              );
            }
            if (event.body && event.body.status) {
              this.results = event.body;
              this.progressInfos[idx]['uploaded'] = event.body.results || [];
            }
            this.updateLoadingStatus();
          }
        },
        () => {
          this.progressInfos[idx].error = true;
          this.progressInfos[idx].complete = true;
          this.progressInfos[idx].value = 0;
          this.progressInfos[idx].message = this.translate.instant(
            'Could not upload the file'
          );
          if (this.progressInfos[idx]['subscription']) {
            this.progressInfos[idx]['subscription'].unsubscribe();
          }
          this.updateLoadingStatus();
        }
      );
  }
}

